import AsyncStorage from "@react-native-async-storage/async-storage";
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import { useColorScheme } from "react-native";
import { darkColors, lightColors, type ThemeColors } from "~/styles/tokens";
import { STORAGE_KEY } from "~/lib/constants";

export {
  borderWidths,
  control,
  counterColorValues,
  fontFamilies,
  fontWeights,
  grid,
  layout,
  radii,
  shadows,
  spacing,
  typography,
  type ThemeColors,
} from "~/styles/tokens";

export type ThemeMode = "light" | "dark";

const THEME_STORAGE_KEY = `${STORAGE_KEY}:theme`;

type ThemeContextValue = {
  colors: ThemeColors;
  mode: ThemeMode;
  setMode(mode: ThemeMode): void;
  toggleMode(): void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: PropsWithChildren) {
  const systemMode = useColorScheme();
  // undefined = still loading storage; null = follow system; light/dark = user choice
  const [storedMode, setStoredMode] = useState<ThemeMode | null | undefined>(
    undefined,
  );

  useEffect(() => {
    let active = true;
    void AsyncStorage.getItem(THEME_STORAGE_KEY).then((saved) => {
      if (!active) return;
      setStoredMode(saved === "light" || saved === "dark" ? saved : null);
    });
    return () => {
      active = false;
    };
  }, []);

  const resolvedMode: ThemeMode =
    storedMode ?? (systemMode === "dark" ? "dark" : "light");

  const setMode = useCallback((nextMode: ThemeMode) => {
    setStoredMode(nextMode);
    void AsyncStorage.setItem(THEME_STORAGE_KEY, nextMode);
  }, []);

  const toggleMode = useCallback(
    () => setMode(resolvedMode === "dark" ? "light" : "dark"),
    [resolvedMode, setMode],
  );

  const value = useMemo(
    () => ({
      colors: resolvedMode === "dark" ? darkColors : lightColors,
      mode: resolvedMode,
      setMode,
      toggleMode,
    }),
    [resolvedMode, setMode, toggleMode],
  );

  return createElement(ThemeContext.Provider, { value }, children);
}

export function useTheme() {
  const value = useContext(ThemeContext);
  if (!value) throw new Error("useTheme must be used inside ThemeProvider");
  return value;
}

export function useThemedStyles<T>(factory: (colors: ThemeColors) => T): T {
  const { colors } = useTheme();
  return useMemo(() => factory(colors), [colors, factory]);
}

/** Light palette snapshot for non-render constants. Prefer useTheme() in UI. */
export const colors: ThemeColors = lightColors;
